import React from 'react';
import './ScheduleView.css';
import {
  IndividualSportEvent,
  IndividualResultEntry,
  MetricType,
  NeverFinishedInfo,
} from '../../types/olympicsListableEvent';
import { formatName } from '../../utils/nameFormatter';
import { getIsoCodeByCountryId } from '../../utils/countryUtils';
import { FlagIcon } from '../FlagIcon/FlagIcon';
import { getTimeDifferenceEventIds } from '../../utils/eventUtils';
import qualifiedCheckmark from './assets/qualified-checkmark.svg';

/**
 * Get metric type display name in Finnish
 */
function getMetricLabel(type: MetricType, eventId: number, year: number): string {
  const labels: Record<MetricType, string> = {
    points: 'Pisteet',
    missed_shots: 'Sakkok.',
    duration: 'Aika',
    distance: 'Etäisyys',
    runningscore: 'Pisteet',
  };

  // For certain events (parisuurpujottelu, yhdistetty), use "Aikaero" instead of "Aika"
  if (type === 'duration') {
    const timeDifferenceEventIds = getTimeDifferenceEventIds(year);
    if (timeDifferenceEventIds.has(eventId)) {
      return 'Aikaero';
    }
  }

  return labels[type];
}

/**
 * Get Finnish text for never finished status
 */
function getNeverFinishedText(status: NeverFinishedInfo): string {
  const labels: Record<NeverFinishedInfo, string> = {
    DNS: 'Ei osallistunut',
    DNF: 'Keskeytti',
    DSQ: 'Hylätty',
  };
  return labels[status];
}

/**
 * Format metric value with Finnish decimal separator (comma instead of dot)
 * Exception: duration (time) values keep the dot separator
 */
function formatMetricValue(value: string, type: MetricType): string {
  if (type === 'duration') {
    return value; // Keep dot for time values
  }
  return value.replace('.', ',');
}

export const ResultsIndividualEvent: React.FC<{
  results: NonNullable<IndividualSportEvent['results']>;
  eventId: number;
  year: number;
}> = ({ results, eventId, year }) => {
  const { bestThree, finnsBelowThree } = results;
  const allResults = [...bestThree, ...finnsBelowThree];

  // Check if any result has qualified status
  const hasQualifiedAthletes = allResults.some(
    (result) => result.standing.type === 'finished' && result.standing.qualified,
  );

  // Determine which metric types exist across all results
  const metricTypes: MetricType[] = [];
  const seenTypes = new Set<MetricType>();

  allResults.forEach((result) => {
    result.metrics.forEach((metric) => {
      if (!seenTypes.has(metric.type)) {
        seenTypes.add(metric.type);
        metricTypes.push(metric.type);
      }
    });
  });

  // Create grid template: rank + flag + name + metric columns (width depends on metric type)
  const gridColumns = `auto auto 1fr ${metricTypes
    .map((type) => (type === 'missed_shots' ? '1.5rem' : '4.6rem'))
    .join(' ')}`;

  const renderResultItem = (result: IndividualResultEntry, key: string | number) => {
    // Create a map for quick lookup of metric values
    const metricsMap = new Map(result.metrics.map((m) => [m.type, m.value]));

    // Determine which column should show the never finished text
    // Rule 1: If there's only one metric column and it's NOT "missed_shots" -> show in that column
    // Rule 2: If there are multiple metric columns -> show in the first column that is NOT "missed_shots"
    // Rule 3: If all columns are "missed_shots" -> don't show the text at all. In real world, this never happens, but that is a logical failsafe in the function.
    const shouldShowNeverFinishedText = (metricType: MetricType): boolean => {
      if (result.standing.type !== 'never_finished') {
        return false;
      }

      if (metricTypes.length === 1) {
        // Only show if the single column is not missed_shots
        return metricType !== 'missed_shots';
      }

      // With multiple columns, show in the first non-missed_shots column only
      const firstNonMissedShotsType = metricTypes.find((type) => type !== 'missed_shots');
      // If no non-missed_shots column exists, don't show the text anywhere
      if (!firstNonMissedShotsType) {
        return false;
      }
      return metricType === firstNonMissedShotsType;
    };

    const getMetricDisplay = (metricType: MetricType): string => {
      // Show never finished status text in appropriate column
      if (shouldShowNeverFinishedText(metricType) && result.standing.type === 'never_finished') {
        return getNeverFinishedText(result.standing.reason);
      }

      // Show metric value if available
      const metricValue = metricsMap.get(metricType);
      if (metricValue) {
        return formatMetricValue(metricValue, metricType);
      }

      return '';
    };

    // Russia (22) and Belarus (57) athletes compete under neutral flag
    const shouldShowNeutralFlag = result.countryId === 22 || result.countryId === 57;
    const isoCode = shouldShowNeutralFlag ? '' : getIsoCodeByCountryId(result.countryId);

    // Check if athlete qualified
    const isQualified = result.standing.type === 'finished' && result.standing.qualified;

    return (
      <React.Fragment key={key}>
        <span className='result-rank'>
          {result.standing.type === 'finished' ? `${result.standing.rank}.` : '-'}
        </span>
        <span className='result-flag'>
          <FlagIcon isoCode={isoCode} />
        </span>
        <span className='result-name'>
          {formatName(result.participantName)}
          {isQualified && <img src={qualifiedCheckmark} alt='' className='qualified-icon' />}
        </span>
        {metricTypes.map((type) => (
          <span key={type} className='result-metric'>
            {getMetricDisplay(type)}
          </span>
        ))}
      </React.Fragment>
    );
  };

  return (
    <div className='event-results' style={{ gridTemplateColumns: gridColumns }}>
      {metricTypes.length > 0 && (
        <>
          <span className='result-ranking-header'>
            Sijoitus
            {hasQualifiedAthletes && (
              <>
                {' '}
                (
                <img src={qualifiedCheckmark} alt='' className='qualified-icon-header' />
                &thinsp;=&thinsp;jatkossa)
              </>
            )}
          </span>
          {metricTypes.map((type) => (
            <span key={type} className='result-metric-header'>
              {getMetricLabel(type, eventId, year)}
            </span>
          ))}
        </>
      )}
      {bestThree.map((p, index) => renderResultItem(p, index))}
      {finnsBelowThree.length > 0 && (
        <>
          <div className='result-divider'></div>
          {finnsBelowThree.map((p, index) => renderResultItem(p, `finn-${index}`))}
        </>
      )}
    </div>
  );
};
